import {
  Paper,
  Skeleton,
  Stack,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format, isValid } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

export interface TableColumn<T> {
  id: keyof T | string;
  label: string;
  align?: "left" | "center" | "right";
  format?: (row: T) => string | ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  dateFormat?: string;
}

export interface ReusableTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  sx?: SxProps<Theme>;
  onRowClick?: (row: T, index: number) => void;
  getRowKey?: (row: T, index: number) => string | number;
  isLoading?: boolean;
  maxHeight?: string | number;
  enableScroll?: boolean;
}

export const ReusableTable = <T,>({
  columns,
  data,
  sx = {},
  onRowClick,
  getRowKey = (_, index) => index,
  isLoading = false,
  maxHeight = "calc(100vh - 200px)",
  enableScroll = true,
}: ReusableTableProps<T>) => {
  const { colors, customFontWeight, radius, typography, customSpacing } =
    useTheme();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: "asc" | "desc" | null;
  }>({ key: null, direction: null });

  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      if (prev.direction === "desc") {
        return { key: null, direction: null };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data;
    }

    return [...data].sort((a, b) => {
      const sortKey = sortConfig.key;
      if (!sortKey) {
        return 0;
      }

      const column = columns.find((c) => c.id === sortKey);

      const aVal = column?.sortValue ? column.sortValue(a) : a[sortKey];
      const bVal = column?.sortValue ? column.sortValue(b) : b[sortKey];

      if (aVal === null || aVal === undefined) {
        return 1;
      }
      if (bVal === null || bVal === undefined) {
        return -1;
      }

      let comparison = 0;

      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal, undefined, {
          sensitivity: "accent",
          numeric: true,
        });
      } else {
        if (aVal < bVal) {
          comparison = -1;
        } else if (aVal > bVal) {
          comparison = 1;
        }
      }
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [data, sortConfig, columns]);

  return (
    <Stack
      sx={{
        borderRadius: radius.xl,
        border: `1px solid ${colors.border.primary}`,
        overflow: "hidden",
        boxShadow: "none",
        userSelect: "none",
        maxHeight: enableScroll ? maxHeight : "none",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          overflow: "hidden",
          borderRadius: 0,
          border: "none",
          flexShrink: 0,
          "& .MuiTableHead-root .MuiTableRow-root": {
            height: 40,
            minHeight: 40,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            height: 40,
            minHeight: 40,
            padding: "8px 16px",
            borderBottom: "none",
            backgroundColor: colors.background.secondary,
            position: "relative",
          },
        }}
      >
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align}
                  sx={{
                    backgroundColor: colors.background.secondary,
                    fontSize: typography.body2.fontSize,
                    fontWeight: customFontWeight.semiBold,
                    cursor: column.sortable ? "pointer" : "default",
                  }}
                  onClick={() =>
                    column.sortable && handleSort(column.id as keyof T)
                  }
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={customSpacing.sm}
                    justifyContent={
                      column.align === "right"
                        ? "flex-end"
                        : column.align === "center"
                          ? "center"
                          : "flex-start"
                    }
                  >
                    {column.label}
                    {column.sortable && (
                      <Stack>
                        <ChevronUp
                          size={12}
                          color={
                            sortConfig.key === column.id &&
                            sortConfig.direction === "asc"
                              ? colors.text.primary
                              : colors.text.disabled
                          }
                        />
                        <ChevronDown
                          size={12}
                          color={
                            sortConfig.key === column.id &&
                            sortConfig.direction === "desc"
                              ? colors.text.primary
                              : colors.text.disabled
                          }
                        />
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflow: enableScroll ? "auto" : "visible",
          boxShadow: "none",
          borderRadius: 0,
          border: "none",
          minHeight: 0,
          "& .MuiTableBody-root .MuiTableRow-root:not(:last-child) .MuiTableCell-root":
            {
              borderBottom: `1px solid ${colors.border.primary}`,
            },
          "& .MuiTableBody-root .MuiTableRow-root": {
            height: "72px",
            minHeight: "72px",
            maxHeight: "72px",
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            height: "72px",
            minHeight: "72px",
            maxHeight: "72px",
            padding: "8px 16px",
            lineHeight: "1.2",
            wordWrap: "break-word",
          },
          ...(enableScroll && {
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.border.secondary,
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: colors.border.primary,
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${colors.background.overlay} transparent`,
          }),
        }}
      >
        <Table sx={{ tableLayout: "fixed" }}>
          <TableBody>
            {isLoading && data.length === 0
              ? Array.from({ length: 5 }, (_, index) => index).map(
                  (skeletonId) => (
                    <TableRow
                      key={`skeleton-row-${skeletonId}`}
                      sx={{
                        backgroundColor: colors.background.primary,
                      }}
                    >
                      {columns.map((column, columnIndex) => (
                        <TableCell
                          key={`skeleton-cell-${skeletonId}-${String(column.id)}`}
                          align={column.align}
                          sx={{
                            fontSize: typography.body2.fontSize,
                            fontWeight: typography.body2.fontWeight,
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={`${60 + columnIndex * 5}%`}
                            height={20}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )
              : sortedData.map((row, index) => (
                  <TableRow
                    key={getRowKey(row, index)}
                    onClick={() => onRowClick?.(row, index)}
                    sx={{
                      backgroundColor: colors.background.primary,
                      "&:hover": {
                        backgroundColor: colors.background.hover,
                      },
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id as keyof T];
                      return (
                        <TableCell
                          key={String(column.id)}
                          align={column.align}
                          sx={{
                            fontSize: typography.body2.fontSize,
                            fontWeight: typography.body2.fontWeight,
                          }}
                        >
                          {column.format
                            ? column.format(row)
                            : column.dateFormat &&
                                value instanceof Date &&
                                isValid(value)
                              ? format(value, column.dateFormat)
                              : String(value ?? "")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
